import { createButton } from "./components";
import type { I18n, TranslationKey } from "../i18n/i18n";

export type InfoDocumentKey = "privacySettings" | "cookiePolicy" | "privacyPolicy" | "termsOfService";

type InfoDocumentSectionSource = {
  heading?: TranslationKey;
  paragraphs?: TranslationKey[];
  bullets?: TranslationKey[];
};

type InfoDocumentSource = {
  title: TranslationKey;
  sections: InfoDocumentSectionSource[];
};

type InfoDocumentSection = {
  heading?: string;
  paragraphs?: string[];
  bullets?: string[];
};

type InfoDocument = {
  title: string;
  sections: InfoDocumentSection[];
};

type MoreInfoOptions = {
  i18n: I18n;
  onBack: () => void;
  onDocument: (documentKey: InfoDocumentKey) => void;
};

type DocumentViewOptions = {
  i18n: I18n;
  documentKey: InfoDocumentKey;
  onBack: () => void;
};

const CONTACT_EMAIL = "mjkierzkowski@gmail.com";
const WEBSITE_URL = "https://michalkierzkowski.com";

export function createMoreInfoView(options: MoreInfoOptions): HTMLElement {
  const section = document.createElement("section");
  section.className = "menu-info-view";

  const grid = document.createElement("div");
  grid.className = "menu-info-grid";

  grid.append(
    createInfoActionButton(options.i18n.t("menu.privacySettings"), () => {
      options.onDocument("privacySettings");
    }),
    createInfoActionButton(options.i18n.t("menu.cookiePolicy"), () => {
      options.onDocument("cookiePolicy");
    }),
    createInfoActionButton(options.i18n.t("menu.privacyPolicy"), () => {
      options.onDocument("privacyPolicy");
    }),
    createInfoActionButton(options.i18n.t("menu.termsOfService"), () => {
      options.onDocument("termsOfService");
    })
  );

  const contactCard = document.createElement("div");
  contactCard.className = "menu-info-contact-card";

  const emailLine = document.createElement("p");
  emailLine.className = "menu-info-contact-line";
  emailLine.textContent = `${options.i18n.t("menu.contactEmailLabel")} ${CONTACT_EMAIL}`;

  const websiteLine = document.createElement("p");
  websiteLine.className = "menu-info-contact-line";
  websiteLine.append(`${options.i18n.t("menu.websiteLabel")} `);

  const websiteLink = document.createElement("a");
  websiteLink.className = "menu-info-link";
  websiteLink.href = WEBSITE_URL;
  websiteLink.target = "_blank";
  websiteLink.rel = "noopener noreferrer";
  websiteLink.textContent = "michalkierzkowski.com";

  websiteLine.append(websiteLink);
  contactCard.append(emailLine, websiteLine);

  const breakElement = document.createElement("br");
  breakElement.className = "menu-info-break";

  const actionsRow = document.createElement("div");
  actionsRow.className = "menu-info-actions";

  const backButton = createButton({
    text: options.i18n.t("menu.back"),
    variant: "secondary",
    onClick: options.onBack
  });

  actionsRow.append(backButton);
  section.append(grid, breakElement, contactCard, actionsRow);

  return section;
}

export function createInfoDocumentView(options: DocumentViewOptions): HTMLElement {
  const documentContent = getInfoDocument(options.documentKey, options.i18n);
  const section = document.createElement("section");
  section.className = "menu-document-view";

  const heading = document.createElement("h2");
  heading.className = "menu-info-title";
  heading.textContent = documentContent.title;

  const scroll = document.createElement("div");
  scroll.className = "menu-document-scroll";

  for (const item of documentContent.sections) {
    const block = document.createElement("div");
    block.className = "menu-document-block";

    if (item.heading) {
      const blockHeading = document.createElement("h3");
      blockHeading.className = "menu-document-heading";
      blockHeading.textContent = item.heading;
      block.append(blockHeading);
    }

    for (const paragraphText of item.paragraphs ?? []) {
      const paragraph = document.createElement("p");
      paragraph.className = "menu-document-paragraph";
      paragraph.textContent = paragraphText;
      block.append(paragraph);
    }

    if (item.bullets && item.bullets.length > 0) {
      const list = document.createElement("ul");
      list.className = "menu-document-list";

      for (const bulletText of item.bullets) {
        const bullet = document.createElement("li");
        bullet.textContent = bulletText;
        list.append(bullet);
      }

      block.append(list);
    }

    scroll.append(block);
  }

  const actionsRow = document.createElement("div");
  actionsRow.className = "menu-info-actions";

  const backButton = createButton({
    text: options.i18n.t("menu.back"),
    variant: "secondary",
    onClick: options.onBack
  });

  actionsRow.append(backButton);
  section.append(heading, scroll, actionsRow);

  return section;
}

function createInfoActionButton(text: string, onClick: () => void): HTMLButtonElement {
  const button = createButton({
    text,
    variant: "secondary",
    onClick
  });
  button.classList.add("menu-info-button");
  return button;
}

function getInfoDocument(documentKey: InfoDocumentKey, i18n: I18n): InfoDocument {
  const source = infoDocuments[documentKey];

  return {
    title: i18n.t(source.title),
    sections: source.sections.map((section) => ({
      heading: section.heading ? i18n.t(section.heading) : undefined,
      paragraphs: section.paragraphs?.map((key) => i18n.t(key)),
      bullets: section.bullets?.map((key) => i18n.t(key))
    }))
  };
}

const infoDocuments: Record<InfoDocumentKey, InfoDocumentSource> = {
  privacySettings: {
    title: "menu.privacySettings",
    sections: [
      {
        paragraphs: [
          "moreInfo.privacySettings.intro1",
          "moreInfo.privacySettings.intro2"
        ]
      },
      {
        heading: "moreInfo.privacySettings.adsHeading",
        paragraphs: [
          "moreInfo.privacySettings.adsParagraph"
        ],
        bullets: [
          "moreInfo.privacySettings.adsBullet1",
          "moreInfo.privacySettings.adsBullet2",
          "moreInfo.privacySettings.adsBullet3"
        ]
      },
      {
        heading: "moreInfo.privacySettings.localHeading",
        paragraphs: [
          "moreInfo.privacySettings.localParagraph"
        ]
      }
    ]
  },
  cookiePolicy: {
    title: "menu.cookiePolicy",
    sections: [
      {
        paragraphs: [
          "moreInfo.cookiePolicy.intro1",
          "moreInfo.cookiePolicy.intro2"
        ]
      },
      {
        heading: "moreInfo.cookiePolicy.typesHeading",
        bullets: [
          "moreInfo.cookiePolicy.typeBullet1",
          "moreInfo.cookiePolicy.typeBullet2",
          "moreInfo.cookiePolicy.typeBullet3"
        ]
      },
      {
        heading: "moreInfo.cookiePolicy.manageHeading",
        paragraphs: [
          "moreInfo.cookiePolicy.manageParagraph"
        ]
      }
    ]
  },
  privacyPolicy: {
    title: "menu.privacyPolicy",
    sections: [
      {
        paragraphs: [
          "moreInfo.privacyPolicy.intro1",
          "moreInfo.privacyPolicy.intro2"
        ]
      },
      {
        heading: "moreInfo.privacyPolicy.infoHeading",
        bullets: [
          "moreInfo.privacyPolicy.infoBullet1",
          "moreInfo.privacyPolicy.infoBullet2",
          "moreInfo.privacyPolicy.infoBullet3"
        ]
      },
      {
        heading: "moreInfo.privacyPolicy.useHeading",
        bullets: [
          "moreInfo.privacyPolicy.useBullet1",
          "moreInfo.privacyPolicy.useBullet2",
          "moreInfo.privacyPolicy.useBullet3",
          "moreInfo.privacyPolicy.useBullet4"
        ]
      },
      {
        heading: "moreInfo.privacyPolicy.contactHeading",
        paragraphs: [
          "moreInfo.privacyPolicy.contactParagraph1",
          "moreInfo.privacyPolicy.contactParagraph2"
        ]
      }
    ]
  },
  termsOfService: {
    title: "menu.termsOfService",
    sections: [
      {
        paragraphs: [
          "moreInfo.terms.intro1",
          "moreInfo.terms.intro2"
        ]
      },
      {
        heading: "moreInfo.terms.rulesHeading",
        bullets: [
          "moreInfo.terms.ruleBullet1",
          "moreInfo.terms.ruleBullet2",
          "moreInfo.terms.ruleBullet3"
        ]
      },
      {
        heading: "moreInfo.terms.limitationHeading",
        paragraphs: [
          "moreInfo.terms.limitationParagraph"
        ]
      },
      {
        heading: "moreInfo.terms.changesHeading",
        paragraphs: [
          "moreInfo.terms.changesParagraph"
        ]
      }
    ]
  }
};
